import type { AndoStorage } from "@ando/core/storage";
import { getEnv } from "@ando/core/env";

/**
 * Concrete `AndoStorage` adapter for `apps/client`.
 *
 * Web target → `window.localStorage` (sync under the hood; the Promise wrapper
 * preserves the cross-target interface so Tauri can swap in
 * `@tauri-apps/plugin-store` without touching call-sites).
 *
 * Tauri target → wired in `apps/desktop` later (Fase 2+). For now we still
 * fall through to localStorage inside the embedded webview, which is
 * acceptable for non-secret state (UI prefs). Secrets must use the OS
 * keychain adapter (Rust `keyring`; separate adapter, not this one).
 */
function localStorageAdapter(): AndoStorage {
  return {
    async get<T>(key: string): Promise<T | null> {
      try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? null : (JSON.parse(raw) as T);
      } catch {
        return null;
      }
    },
    async set<T>(key: string, value: T): Promise<void> {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Quota exceeded or private mode — silently drop. Caller can re-attempt.
      }
    },
    async remove(key: string): Promise<void> {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
    async clear(): Promise<void> {
      try {
        window.localStorage.clear();
      } catch {
        // ignore
      }
    },
  };
}

function memoryAdapter(): AndoStorage {
  const map = new Map<string, unknown>();
  return {
    async get<T>(key: string) {
      return (map.get(key) as T | undefined) ?? null;
    },
    async set<T>(key: string, value: T) {
      map.set(key, value);
    },
    async remove(key: string) {
      map.delete(key);
    },
    async clear() {
      map.clear();
    },
  };
}

let cached: AndoStorage | null = null;

export function getStorage(): AndoStorage {
  if (cached) return cached;
  const env = getEnv();
  // Tauri-specific adapter will land in Fase 2 (`@tauri-apps/plugin-store`).
  // For now both web + Tauri webview use localStorage; the OS keychain handles secrets.
  void env;
  cached =
    typeof window !== "undefined" && "localStorage" in window
      ? localStorageAdapter()
      : memoryAdapter();
  return cached;
}

/**
 * Zustand-compatible synchronous wrapper around `AndoStorage` for
 * `persist({ storage: createJSONStorage(() => zustandStorage) })`.
 *
 * Web target reads/writes synchronously against localStorage; Tauri target
 * (later) will need a different shim because plugin-store is async. We expose
 * the sync flavour here and gate Tauri integration in `apps/desktop`.
 */
export const zustandStorage = {
  getItem: (name: string): string | null => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  },
  removeItem: (name: string): void => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};
